import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { invalidateGrades, invalidateSubjects, invalidateTopics } from '@/lib/curriculumCache';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, ChevronRight, ChevronDown, BookOpen, GraduationCap, FileText, Brain, Loader2, X, FileUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatJamaicaDate as formatDate } from '@/lib/datetime';

interface Grade {
  id: string;
  gradeNumber: number;
  displayName: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface Subject {
  id: string;
  gradeId: string;
  name: string;
  displayName: string;
  icon?: string;
  description?: string;
  isActive: boolean;
  sortOrder: number;
  trainingText?: string | null;
  trainingFiles?: Array<{filename: string, mimeType: string, base64: string, uploadedAt: string}> | null;
  createdAt: string;
  updatedAt: string;
  grade?: Grade;
}

interface Topic {
  id: string;
  subjectId: string;
  name: string;
  displayName: string;
  icon?: string;
  description?: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  subject?: Subject;
}

export default function CurriculumManager() {
  const { toast } = useToast();
  const [expandedGrades, setExpandedGrades] = useState<Set<string>>(new Set());
  const [expandedSubjects, setExpandedSubjects] = useState<Set<string>>(new Set());
  
  // Fetch data
  const { data: grades = [], isLoading: gradesLoading } = useQuery<Grade[]>({
    queryKey: ['/api/admin/grades'],
  });

  const { data: subjects = [], isLoading: subjectsLoading } = useQuery<Subject[]>({
    queryKey: ['/api/admin/subjects'],
  });

  const { data: topics = [], isLoading: topicsLoading } = useQuery<Topic[]>({
    queryKey: ['/api/admin/topics'],
  });

  // Fetch question counts per topic
  const { data: topicQuestionCounts = {} } = useQuery<Record<string, number>>({
    queryKey: ['/api/admin/topics/question-counts'],
  });

  const toggleGrade = (gradeId: string) => {
    const newExpanded = new Set(expandedGrades);
    if (newExpanded.has(gradeId)) {
      newExpanded.delete(gradeId);
    } else {
      newExpanded.add(gradeId);
    }
    setExpandedGrades(newExpanded);
  };

  const toggleSubject = (subjectId: string) => {
    const newExpanded = new Set(expandedSubjects);
    if (newExpanded.has(subjectId)) {
      newExpanded.delete(subjectId);
    } else {
      newExpanded.add(subjectId);
    }
    setExpandedSubjects(newExpanded);
  };

  const getSubjectsForGrade = (gradeId: string) => {
    return subjects.filter(s => s.gradeId === gradeId).sort((a, b) => a.sortOrder - b.sortOrder);
  };

  const getTopicsForSubject = (subjectId: string) => {
    return topics.filter(t => t.subjectId === subjectId).sort((a, b) => a.sortOrder - b.sortOrder);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold" data-testid="text-curriculum-manager-title">Curriculum Management</h2>
          <p className="text-muted-foreground">Manage grades, subjects, and topics in a hierarchical structure</p>
        </div>
        <GradeDialog onSuccess={() => queryClient.invalidateQueries({ queryKey: ['/api/admin/grades'] })} />
      </div>

      {gradesLoading ? (
        <div className="text-center py-8">Loading...</div>
      ) : grades.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <GraduationCap className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No grades created yet</p>
            <GradeDialog onSuccess={() => queryClient.invalidateQueries({ queryKey: ['/api/admin/grades'] })} />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {grades.sort((a, b) => a.sortOrder - b.sortOrder).map(grade => {
            const isExpanded = expandedGrades.has(grade.id);
            const gradeSubjects = getSubjectsForGrade(grade.id);

            return (
              <Card key={grade.id} className="overflow-hidden">
                <CardHeader className="bg-muted/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleGrade(grade.id)}
                        data-testid={`button-toggle-grade-${grade.id}`}
                      >
                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </Button>
                      <GraduationCap className="h-5 w-5 text-primary" />
                      <div className="flex-1">
                        <CardTitle className="text-lg">{grade.displayName}</CardTitle>
                        <CardDescription>Grade {grade.gradeNumber}</CardDescription>
                      </div>
                      <Badge variant={grade.isActive ? "default" : "secondary"}>
                        {grade.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      <div className="flex gap-2">
                        <GradeDialog 
                          grade={grade} 
                          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['/api/admin/grades'] })} 
                        />
                        <DeleteGradeButton grade={grade} />
                      </div>
                    </div>
                  </div>
                </CardHeader>

                {isExpanded && (
                  <CardContent className="pt-6 pl-12">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-sm text-muted-foreground">SUBJECTS IN THIS GRADE</h4>
                        <SubjectDialog 
                          gradeId={grade.id} 
                          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['/api/admin/subjects'] })} 
                        />
                      </div>

                      {gradeSubjects.length === 0 ? (
                        <div className="text-center py-6 text-muted-foreground text-sm">
                          No subjects created for this grade yet
                        </div>
                      ) : (
                        gradeSubjects.map(subject => {
                          const isSubjectExpanded = expandedSubjects.has(subject.id);
                          const subjectTopics = getTopicsForSubject(subject.id);

                          return (
                            <Card key={subject.id} className="ml-4">
                              <CardHeader className="bg-muted/30">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3 flex-1">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => toggleSubject(subject.id)}
                                      data-testid={`button-toggle-subject-${subject.id}`}
                                    >
                                      {isSubjectExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                    </Button>
                                    <BookOpen className="h-5 w-5 text-primary" />
                                    <div className="flex-1">
                                      <CardTitle className="text-base">{subject.displayName}</CardTitle>
                                      {subject.description && (
                                        <CardDescription className="text-xs">{subject.description}</CardDescription>
                                      )}
                                    </div>
                                    <Badge variant={subject.isActive ? "default" : "secondary"} className="text-xs">
                                      {subject.isActive ? 'Active' : 'Inactive'}
                                    </Badge>
                                    <div className="flex gap-2">
                                      <SubjectDialog 
                                        subject={subject} 
                                        gradeId={grade.id}
                                        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['/api/admin/subjects'] })} 
                                      />
                                      <DeleteSubjectButton subject={subject} />
                                    </div>
                                  </div>
                                </div>
                              </CardHeader>

                              {isSubjectExpanded && (
                                <CardContent className="pt-4 pl-10">
                                  <div className="space-y-2">
                                    <div className="flex items-center justify-between mb-3">
                                      <h5 className="font-semibold text-xs text-muted-foreground">TOPICS IN THIS SUBJECT</h5>
                                      <TopicDialog 
                                        subjectId={subject.id} 
                                        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['/api/admin/topics'] })} 
                                      />
                                    </div>

                                    {subjectTopics.length === 0 ? (
                                      <div className="text-center py-4 text-muted-foreground text-xs">
                                        No topics created for this subject yet
                                      </div>
                                    ) : (
                                      <div className="space-y-2">
                                        {subjectTopics.map(topic => {
                                          const questionCount = topicQuestionCounts[topic.id] || 0;
                                          return (
                                          <Card key={topic.id} className="ml-4">
                                            <CardContent className="p-3">
                                              <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2 flex-1">
                                                  <FileText className="h-4 w-4 text-primary" />
                                                  <div className="flex-1">
                                                    <p className="font-medium text-sm">{topic.displayName}</p>
                                                    {topic.description && (
                                                      <p className="text-xs text-muted-foreground">{topic.description}</p>
                                                    )}
                                                  </div>
                                                  <Badge variant="outline" className="text-xs">
                                                    {questionCount} {questionCount === 1 ? 'question' : 'questions'}
                                                  </Badge>
                                                  <Badge variant={topic.isActive ? "default" : "secondary"} className="text-xs">
                                                    {topic.isActive ? 'Active' : 'Inactive'}
                                                  </Badge>
                                                  <div className="flex gap-1">
                                                    <TopicDialog 
                                                      topic={topic} 
                                                      subjectId={subject.id}
                                                      onSuccess={() => queryClient.invalidateQueries({ queryKey: ['/api/admin/topics'] })} 
                                                    />
                                                    <DeleteTopicButton topic={topic} />
                                                  </div>
                                                </div>
                                              </div>
                                            </CardContent>
                                          </Card>
                                        );})}
                                      </div>
                                    )}

                                    <TrainingTextSection subject={subject} />
                                  </div>
                                </CardContent>
                              )}
                            </Card>
                          );
                        })
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GradeDialog({ grade, onSuccess }: { grade?: Grade; onSuccess: () => void }) {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    gradeNumber: grade?.gradeNumber || 0,
    displayName: grade?.displayName || '',
    isActive: grade?.isActive ?? true,
    sortOrder: grade?.sortOrder || 0,
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return await apiRequest('POST', '/api/admin/grades', data);
    },
    onSuccess: () => {
      invalidateGrades();
      toast({ title: 'Success', description: 'Grade created successfully' });
      setIsOpen(false);
      onSuccess();
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to create grade', variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return await apiRequest('PUT', `/api/admin/grades/${grade!.id}`, data);
    },
    onSuccess: () => {
      invalidateGrades();
      toast({ title: 'Success', description: 'Grade updated successfully' });
      setIsOpen(false);
      onSuccess();
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to update grade', variant: 'destructive' });
    },
  });

  const handleSubmit = () => {
    if (grade) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {grade ? (
          <Button variant="ghost" size="icon" data-testid={`button-edit-grade-${grade.id}`}>
            <Edit className="h-4 w-4" />
          </Button>
        ) : (
          <Button data-testid="button-add-grade">
            <Plus className="h-4 w-4 mr-2" />
            Add Grade
          </Button>
        )}
      </DialogTrigger>
      <DialogContent data-testid="dialog-grade-form">
        <DialogHeader>
          <DialogTitle>{grade ? 'Edit Grade' : 'Add New Grade'}</DialogTitle>
          <DialogDescription>
            {grade ? 'Update the grade details' : 'Create a new grade level'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="gradeNumber">Grade Number</Label>
            <Input
              id="gradeNumber"
              type="number"
              value={formData.gradeNumber}
              onChange={(e) => setFormData({ ...formData, gradeNumber: parseInt(e.target.value) || 0 })}
              data-testid="input-grade-number"
            />
          </div>
          <div>
            <Label htmlFor="displayName">Display Name</Label>
            <Input
              id="displayName"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              placeholder="e.g., Grade 4"
              data-testid="input-grade-display-name"
            />
          </div>
          <div>
            <Label htmlFor="sortOrder">Sort Order</Label>
            <Input
              id="sortOrder"
              type="number"
              value={formData.sortOrder}
              onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
              data-testid="input-grade-sort-order"
            />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="isActive">Active</Label>
            <Switch
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              data-testid="switch-grade-active"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending} data-testid="button-submit-grade">
            {grade ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteGradeButton({ grade }: { grade: Grade }) {
  const { toast } = useToast();

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest('DELETE', `/api/admin/grades/${grade.id}`);
    },
    onSuccess: () => {
      invalidateGrades();
      toast({ title: 'Success', description: 'Grade deleted successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to delete grade', variant: 'destructive' });
    },
  });

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => {
        if (confirm('Are you sure you want to delete this grade? This will also delete all subjects and topics within it.')) {
          deleteMutation.mutate();
        }
      }}
      data-testid={`button-delete-grade-${grade.id}`}
    >
      <Trash2 className="h-4 w-4 text-destructive" />
    </Button>
  );
}

function SubjectDialog({ subject, gradeId, onSuccess }: { subject?: Subject; gradeId: string; onSuccess: () => void }) {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    gradeId: subject?.gradeId || gradeId,
    name: subject?.name || '',
    displayName: subject?.displayName || '',
    icon: subject?.icon || '',
    description: subject?.description || '',
    isActive: subject?.isActive ?? true,
    sortOrder: subject?.sortOrder || 0,
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return await apiRequest('POST', '/api/admin/subjects', data);
    },
    onSuccess: () => {
      invalidateSubjects();
      toast({ title: 'Success', description: 'Subject created successfully' });
      setIsOpen(false);
      onSuccess();
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to create subject', variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return await apiRequest('PUT', `/api/admin/subjects/${subject!.id}`, data);
    },
    onSuccess: () => {
      invalidateSubjects();
      toast({ title: 'Success', description: 'Subject updated successfully' });
      setIsOpen(false);
      onSuccess();
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to update subject', variant: 'destructive' });
    },
  });

  const handleSubmit = () => {
    if (subject) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {subject ? (
          <Button variant="ghost" size="icon" data-testid={`button-edit-subject-${subject.id}`}>
            <Edit className="h-4 w-4" />
          </Button>
        ) : (
          <Button size="sm" data-testid={`button-add-subject-${gradeId}`}>
            <Plus className="h-3 w-3 mr-1" />
            Add Subject
          </Button>
        )}
      </DialogTrigger>
      <DialogContent data-testid="dialog-subject-form">
        <DialogHeader>
          <DialogTitle>{subject ? 'Edit Subject' : 'Add New Subject'}</DialogTitle>
          <DialogDescription>
            {subject ? 'Update the subject details' : 'Create a new subject for this grade'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Name (internal)</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., math"
              data-testid="input-subject-name"
            />
          </div>
          <div>
            <Label htmlFor="displayName">Display Name</Label>
            <Input
              id="displayName"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              placeholder="e.g., Mathematics"
              data-testid="input-subject-display-name"
            />
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description..."
              data-testid="input-subject-description"
            />
          </div>
          <div>
            <Label htmlFor="sortOrder">Sort Order</Label>
            <Input
              id="sortOrder"
              type="number"
              value={formData.sortOrder}
              onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
              data-testid="input-subject-sort-order"
            />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="isActive">Active</Label>
            <Switch
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              data-testid="switch-subject-active"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending} data-testid="button-submit-subject">
            {subject ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteSubjectButton({ subject }: { subject: Subject }) {
  const { toast } = useToast();

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest('DELETE', `/api/admin/subjects/${subject.id}`);
    },
    onSuccess: () => {
      invalidateSubjects();
      toast({ title: 'Success', description: 'Subject deleted successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to delete subject', variant: 'destructive' });
    },
  });

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => {
        if (confirm('Are you sure you want to delete this subject? This will also delete all topics within it.')) {
          deleteMutation.mutate();
        }
      }}
      data-testid={`button-delete-subject-${subject.id}`}
    >
      <Trash2 className="h-4 w-4 text-destructive" />
    </Button>
  );
}

function TopicDialog({ topic, subjectId, onSuccess }: { topic?: Topic; subjectId: string; onSuccess: () => void }) {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    subjectId: topic?.subjectId || subjectId,
    name: topic?.name || '',
    displayName: topic?.displayName || '',
    icon: topic?.icon || '',
    description: topic?.description || '',
    isActive: topic?.isActive ?? true,
    sortOrder: topic?.sortOrder || 0,
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return await apiRequest('POST', '/api/admin/topics', data);
    },
    onSuccess: () => {
      invalidateTopics();
      toast({ title: 'Success', description: 'Topic created successfully' });
      setIsOpen(false);
      onSuccess();
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to create topic', variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return await apiRequest('PUT', `/api/admin/topics/${topic!.id}`, data);
    },
    onSuccess: () => {
      invalidateTopics();
      toast({ title: 'Success', description: 'Topic updated successfully' });
      setIsOpen(false);
      onSuccess();
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to update topic', variant: 'destructive' });
    },
  });

  const handleSubmit = () => {
    if (topic) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {topic ? (
          <Button variant="ghost" size="icon" data-testid={`button-edit-topic-${topic.id}`}>
            <Edit className="h-3 w-3" />
          </Button>
        ) : (
          <Button size="sm" data-testid={`button-add-topic-${subjectId}`}>
            <Plus className="h-3 w-3 mr-1" />
            Add Topic
          </Button>
        )}
      </DialogTrigger>
      <DialogContent data-testid="dialog-topic-form">
        <DialogHeader>
          <DialogTitle>{topic ? 'Edit Topic' : 'Add New Topic'}</DialogTitle>
          <DialogDescription>
            {topic ? 'Update the topic details' : 'Create a new topic for this subject'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Name (internal)</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., fractions"
              data-testid="input-topic-name"
            />
          </div>
          <div>
            <Label htmlFor="displayName">Display Name</Label>
            <Input
              id="displayName"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              placeholder="e.g., Fractions"
              data-testid="input-topic-display-name"
            />
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description..."
              data-testid="input-topic-description"
            />
          </div>
          <div>
            <Label htmlFor="sortOrder">Sort Order</Label>
            <Input
              id="sortOrder"
              type="number"
              value={formData.sortOrder}
              onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
              data-testid="input-topic-sort-order"
            />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="isActive">Active</Label>
            <Switch
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              data-testid="switch-topic-active"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending} data-testid="button-submit-topic">
            {topic ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteTopicButton({ topic }: { topic: Topic }) {
  const { toast } = useToast();

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest('DELETE', `/api/admin/topics/${topic.id}`);
    },
    onSuccess: () => {
      invalidateTopics();
      toast({ title: 'Success', description: 'Topic deleted successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to delete topic', variant: 'destructive' });
    },
  });

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => {
        if (confirm('Are you sure you want to delete this topic?')) {
          deleteMutation.mutate();
        }
      }}
      data-testid={`button-delete-topic-${topic.id}`}
    >
      <Trash2 className="h-3 w-3 text-destructive" />
    </Button>
  );
}

function TrainingTextSection({ subject }: { subject: Subject }) {
  const { toast } = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeletingIndex, setIsDeletingIndex] = useState<number | null>(null);

  const trainingFiles = subject.trainingFiles || [];

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'text/plain',
    ];
    const allowedExtensions = ['.pdf', '.docx', '.doc', '.txt'];
    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExt)) {
      toast({ title: 'Unsupported file type', description: 'Please upload a PDF, Word document (.docx/.doc), or text file (.txt).', variant: 'destructive' });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum file size is 10MB.', variant: 'destructive' });
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', 'append');

      const res = await fetch(`/api/admin/subjects/${subject.id}/training-upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Upload failed');
      }

      const data = await res.json();
      queryClient.invalidateQueries({ queryKey: ['/api/admin/subjects'] });
      const sizeKB = Math.round(data.fileSize / 1024);
      toast({
        title: `Uploaded "${file.name}"`,
        description: `${sizeKB}KB stored. ${data.fileCount} training document(s) total. The AI will read this document directly when importing questions.`,
      });
    } catch (error: any) {
      toast({ title: 'Upload failed', description: error.message, variant: 'destructive' });
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteFile = async (fileIndex: number) => {
    setIsDeletingIndex(fileIndex);
    try {
      const res = await fetch(`/api/admin/subjects/${subject.id}/training-file/${fileIndex}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Delete failed');
      }
      const data = await res.json();
      queryClient.invalidateQueries({ queryKey: ['/api/admin/subjects'] });
      toast({ title: `Removed "${data.removedFile}"`, description: `${data.fileCount} training document(s) remaining.` });
    } catch (error: any) {
      toast({ title: 'Delete failed', description: error.message, variant: 'destructive' });
    } finally {
      setIsDeletingIndex(null);
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType === 'application/pdf') return 'PDF';
    if (mimeType.includes('word') || mimeType.includes('officedocument')) return 'DOC';
    return 'TXT';
  };

  return (
    <div className="mt-4 border-t pt-4">
      <button
        className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-full text-left"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Brain className="h-4 w-4 text-primary" />
        AI Training Documents
        {trainingFiles.length > 0 && <Badge variant="secondary" className="text-xs ml-1">{trainingFiles.length} file{trainingFiles.length !== 1 ? 's' : ''}</Badge>}
        {isExpanded ? <ChevronDown className="h-3 w-3 ml-auto" /> : <ChevronRight className="h-3 w-3 ml-auto" />}
      </button>
      {isExpanded && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-muted-foreground">
            Upload training documents (PDF, Word, text files) that the AI will read directly when importing questions.
            The AI uses these documents to understand topic structure and accurately classify questions.
          </p>

          {trainingFiles.length > 0 && (
            <div className="space-y-1.5">
              {trainingFiles.map((file, index) => (
                <div key={index} className="flex items-center gap-2 py-1.5 px-2 rounded-md bg-muted/50 text-sm">
                  <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0 no-default-active-elevate">
                    {getFileIcon(file.mimeType)}
                  </Badge>
                  <span className="flex-1 truncate text-xs">{file.filename}</span>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                    {formatDate(file.uploadedAt)}
                  </span>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    onClick={() => handleDeleteFile(index)}
                    disabled={isDeletingIndex === index}
                  >
                    {isDeletingIndex === index ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <X className="h-3 w-3" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".pdf,.docx,.doc,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/plain"
                onChange={handleFileUpload}
                className="hidden"
                disabled={isUploading}
                data-testid={`input-training-file-${subject.id}`}
              />
              <Button
                size="sm"
                variant="outline"
                disabled={isUploading}
                asChild
              >
                <span>
                  {isUploading ? (
                    <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" />Uploading...</>
                  ) : (
                    <><FileUp className="h-3 w-3 mr-1.5" />Upload Document</>
                  )}
                </span>
              </Button>
            </label>
            <span className="text-xs text-muted-foreground">PDF, Word (.docx/.doc), or text file (max 10MB)</span>
          </div>
        </div>
      )}
    </div>
  );
}
